// components/massEmail/AudienceTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  LinearProgress, Pagination, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import RemoveDoneIcon from "@mui/icons-material/RemoveDone";
import { emailContactsApi } from "./api/emailContactsApi";
import { toAudienceRow } from "./audienceUtils";

const SOURCE_LABELS = {
  email_contacts: "Email Contacts",
  import: "Imported List",
  manual: "Manual",
  raw_list: "CSV/Excel Import",
};

function formatLastContacted(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return null;
  const diffMs = Date.now() - d.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function ContactedBadge({ dateStr }) {
  if (!dateStr) {
    return (
      <Chip
        size="small"
        label="Never"
        sx={{ bgcolor: "#F0FDF4", color: "#15803D", fontWeight: 700, fontSize: "0.7rem", height: 20 }}
      />
    );
  }
  const diffDays = Math.floor((Date.now() - new Date(dateStr).getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays <= 7) {
    return (
      <Tooltip title={`Last contacted ${formatLastContacted(dateStr)} — may be suppressed`} arrow>
        <Chip size="small" label={formatLastContacted(dateStr)}
          sx={{ bgcolor: "#FEF2F2", color: "#DC2626", fontWeight: 700, fontSize: "0.7rem", height: 20 }} />
      </Tooltip>
    );
  }
  if (diffDays <= 30) {
    return (
      <Tooltip title={`Last contacted ${formatLastContacted(dateStr)}`} arrow>
        <Chip size="small" label={formatLastContacted(dateStr)}
          sx={{ bgcolor: "#FFFBEB", color: "#D97706", fontWeight: 700, fontSize: "0.7rem", height: 20 }} />
      </Tooltip>
    );
  }
  return (
    <Tooltip title={`Last contacted ${formatLastContacted(dateStr)}`} arrow>
      <Chip size="small" label={formatLastContacted(dateStr)}
        sx={{ bgcolor: "#F0F9FF", color: "#0369A1", fontWeight: 700, fontSize: "0.7rem", height: 20 }} />
    </Tooltip>
  );
}

export default function AudienceTable({
  source, filters,
  audienceById, setAudienceById,
  selectedIds, setSelectedIds,
  previewTotalFound, previewRows, estimateSummary,
  suppressedEmails = [],
  excludeRecent = true,
  recentDays = 7,
}) {
  const [page, setPage] = useState(1);
  const [perPage] = useState(100);
  const [totalContacts, setTotalContacts] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pageRows, setPageRows] = useState([]);

  const totalPages = Math.max(1, Math.ceil(totalContacts / perPage));

  // Build suppressed email set for quick lookup
  const suppressedSet = useMemo(() => {
    const s = new Set();
    (suppressedEmails || []).forEach(item => {
      if (item?.email) s.add(item.email.toLowerCase());
    });
    return s;
  }, [suppressedEmails]);

  const suppressedIdSet = useMemo(() => {
    const s = new Set();
    pageRows.forEach((r) => {
      if (suppressedSet.has(r.email?.toLowerCase())) s.add(r.id);
    });
    return s;
  }, [pageRows, suppressedSet]);

  const eligibleOnPage = useMemo(
    () => pageRows.filter((r) => !suppressedIdSet.has(r.id)),
    [pageRows, suppressedIdSet]
  );

  const allCheckedOnPage = eligibleOnPage.length > 0 && eligibleOnPage.every((r) => selectedIds.includes(r.id));
  const someCheckedOnPage = eligibleOnPage.some((r) => selectedIds.includes(r.id));

  const toggleAllOnPage = (checked) => {
    if (checked) {
      const next = new Set(selectedIds);
      eligibleOnPage.forEach((r) => next.add(r.id));
      setSelectedIds(Array.from(next));
    } else {
      const remove = new Set(pageRows.map((r) => r.id));
      setSelectedIds(selectedIds.filter((id) => !remove.has(id)));
    }
  };

  const toggleOne = (id) => {
    if (suppressedIdSet.has(id)) return;
    setSelectedIds((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return Array.from(s);
    });
  };

  const [clearAllConfirmOpen, setClearAllConfirmOpen] = useState(false);
  const clearAll = () => {
    if (selectedIds.length > 10) {
      setClearAllConfirmOpen(true);
    } else {
      setSelectedIds([]);
    }
  };

  // Load page from server
  useEffect(() => {
    if (source !== "email_contacts") return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await emailContactsApi.list({
          q: filters.q || "",
          source: filters.source || "",
          segment: filters.segment || "",
          active: "1",
          excludeRecent,
          days: recentDays,
          perPage,
          page,
        });

        const paginated = res?.contacts || res;
        const rows = Array.isArray(paginated?.data) ? paginated.data : [];
        const mapped = rows.map(toAudienceRow);

        if (!alive) return;
        setPageRows(mapped);
        // Use Laravel pagination meta for accurate total
        const meta = paginated?.meta || paginated;
        if (meta?.total) setTotalContacts(Number(meta.total));
        else if (previewTotalFound) setTotalContacts(previewTotalFound);

        setAudienceById((prev) => {
          const next = new Map(prev);
          mapped.forEach((r) => next.set(r.id, r));
          return next;
        });
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [source, filters.q, filters.source, filters.segment, excludeRecent, recentDays, page, perPage]);

  useEffect(() => {
    if (source !== "import") return;
    setPageRows(previewRows || []);
    setTotalContacts(previewRows?.length || 0);
  }, [source, previewRows]);

  // Keep totalContacts in sync with estimate summary
  useEffect(() => {
    if (estimateSummary?.totalFound) setTotalContacts(estimateSummary.totalFound);
  }, [estimateSummary]);

  const selectedCount = selectedIds.length;
  const showSourceCol = source === "email_contacts" || source === "import";

  return (
    <Box sx={{ width: "100%", maxWidth: "100%" }}>
      {loading ? <LinearProgress sx={{ mb: 1, borderRadius: 1 }} /> : null}

      {/* ── Header row ── */}
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between" flexWrap="wrap" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={900} sx={{ mb: 0.25 }}>Review recipients</Typography>
          <Typography variant="body2" color="text.secondary">
            Select contacts before sending.{" "}
            {totalContacts > perPage && (
              <span>Showing page {page} of {totalPages} — {totalContacts.toLocaleString()} total contacts.</span>
            )}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          {source === "email_contacts" && (
            <Chip label={estimateSummary ? `Matched: ${Number(estimateSummary.totalFound || previewTotalFound || 0).toLocaleString()}` : `Total: ${totalContacts.toLocaleString()}`} />
          )}
          {estimateSummary && (
            <>
              <Chip color="primary" label={`Eligible: ${Number(estimateSummary.eligibleFound || 0).toLocaleString()}`} />
              <Chip color="warning" label={`Suppressed: ${Number(estimateSummary.suppressedFound || 0).toLocaleString()}`} />
            </>
          )}
          <Chip label={`Selected: ${selectedCount.toLocaleString()}`} color={selectedCount ? "success" : "default"} sx={{ fontWeight: 900 }} />
          {selectedCount > 0 && (
            <Tooltip title="Clear all selections" arrow>
              <Button size="small" startIcon={<RemoveDoneIcon />} onClick={clearAll}
                sx={{ textTransform: "none", fontWeight: 700, color: "text.secondary" }}>
                Clear
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      {/* ── Table ── */}
      <TableContainer component={Paper} elevation={0} sx={{ width: "100%", border: "1px solid #E9ECEF", borderRadius: 2, bgcolor: "white", overflow: "hidden" }}>
        <Box sx={{ maxHeight: 560, overflow: "auto" }}>
          <Table stickyHeader size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ width: 48 }}>
                  <Tooltip title={allCheckedOnPage ? "Deselect page" : eligibleOnPage.length < pageRows.length ? "Select all eligible on this page" : "Select page"} arrow>
                    <Checkbox
                      checked={allCheckedOnPage}
                      indeterminate={!allCheckedOnPage && someCheckedOnPage}
                      onChange={(e) => toggleAllOnPage(e.target.checked)}
                    />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ fontWeight: 900, width: "24%" }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 900, width: showSourceCol ? "38%" : "52%" }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 900, width: "16%" }}>
                  <Tooltip title="🟢 Never · 🔴 ≤7 days · 🟠 ≤30 days · 🔵 30+ days" arrow>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
                      Last Contacted <InfoOutlinedIcon sx={{ fontSize: 13, ml: 0.25 }} />
                    </span>
                  </Tooltip>
                </TableCell>
                {showSourceCol && <TableCell sx={{ fontWeight: 900, width: "14%" }}>Source</TableCell>}
                <TableCell sx={{ fontWeight: 900, width: "8%" }}>Status</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {pageRows.map((r) => {
                const checked = selectedIds.includes(r.id);
                const isSuppressed = suppressedSet.has(r.email.toLowerCase());
                return (
                  <TableRow
                    key={r.id}
                    hover
                    selected={checked}
                    onClick={() => toggleOne(r.id)}
                    sx={{
                      cursor: "pointer",
                      opacity: isSuppressed ? 0.45 : 1,
                      bgcolor: isSuppressed ? "#FFF8F8" : undefined,
                    }}
                  >
                    <TableCell padding="checkbox" sx={isSuppressed ? { pointerEvents: "none" } : undefined}>
                      <Tooltip title={isSuppressed ? "This contact is suppressed and will not receive this email" : ""} arrow disableHoverListener={!isSuppressed}>
                        <span>
                          <Checkbox checked={checked} disabled={isSuppressed} />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.name}>
                      {r.name}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={r.email}>
                      {r.email}
                    </TableCell>
                    <TableCell>
                      <ContactedBadge dateStr={r.last_contacted_at} />
                    </TableCell>
                    {showSourceCol && (
                      <TableCell sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }} title={SOURCE_LABELS[r._source] || r._source}>
                        {SOURCE_LABELS[r._source] || r._source}
                      </TableCell>
                    )}
                    <TableCell>
                      {isSuppressed ? (
                        <Tooltip title="Recently contacted — will be suppressed at send" arrow>
                          <Chip size="small" label="Suppressed" sx={{ bgcolor: "#FEF2F2", color: "#DC2626", fontSize: "0.65rem", height: 18, fontWeight: 700 }} />
                        </Tooltip>
                      ) : (
                        <Chip size="small" label="OK" sx={{ bgcolor: "#F0FDF4", color: "#15803D", fontSize: "0.65rem", height: 18, fontWeight: 700 }} />
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}

              {!pageRows.length && !loading && (
                <TableRow>
                  <TableCell colSpan={showSourceCol ? 6 : 5}>
                    <Typography align="center" color="text.secondary" sx={{ py: 3 }}>
                      No contacts found. Try adjusting your filters or running an estimate first.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </TableContainer>

      {/* ── Pagination with total info ── */}
      {source === "email_contacts" && totalPages > 1 && (
        <Box sx={{ mt: 2, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1 }}>
          <Typography variant="caption" color="text.secondary">
            Page {page} of {totalPages} — {totalContacts.toLocaleString()} contacts total
          </Typography>
          <Pagination
            page={page}
            count={totalPages}
            onChange={(_, p) => setPage(p)}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      <Dialog open={clearAllConfirmOpen} onClose={() => setClearAllConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clear all selections?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove {selectedIds.length.toLocaleString()} selected contacts.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClearAllConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => { setSelectedIds([]); setClearAllConfirmOpen(false); }}
            color="error"
            variant="contained"
          >
            Clear All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
